import React, { Fragment, useEffect, useState } from 'react'
import { Button, LoadingSpinner, PreferenceCard, PreferenceFormModal, ConfirmationModal } from '../components'
import {
    API_BASE_URL, PreferenceCardMeta, LoadingSpinnerMeta,
    PreferenceFormModalMeta, UpdatePreferenceFormMeta, ConfirmationModalMeta, DeletePreferenceMeta
} from '../data'
import { Preference, PreferenceMetaProps } from '../data/types'
import { PlusIcon, ChevronLeftIcon } from '../icons'
import { Link } from 'react-router-dom'
import { useResourcePermissions } from '../hooks/useResourcePermissions'

type Props = {
    meta: PreferenceMetaProps,
}

export const Preferences = (props: Props) => {
    const { meta } = props
    const { canEditPreference, canDeletePreference, canCreatePreference } = useResourcePermissions()
    const [loading, setLoading] = useState(true)
    const [preferences, setPreferences] = useState<Preference[]>([])
    const [error, setError] = useState<string | null>(null)
    const [isCreatePreferenceModalOpen, setIsCreatePreferenceModalOpen] = useState(false)
    const [selectedPreference, setSelectedPreference] = useState<Preference | null>(null)
    const [isEditPreferenceModalOpen, setIsEditPreferenceModalOpen] = useState(false)
    const [isDeletePreferenceModalOpen, setIsDeletePreferenceModalOpen] = useState(false)
    const [isDeletingPreference, setIsDeletingPreference] = useState(false)
    const [filterText, setFilterText] = useState<string | null>(null)

    useEffect(() => {
        const fetchPreferences = async () => {
            setLoading(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/preference`, { credentials: 'include' })
                if (!response.ok) {
                    const text = await response.text()
                    const textObj = JSON.parse(text)
                    throw new Error(`${textObj.message}`)
                }
                const result = await response.json()
                setPreferences(result)
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching preferences')
            }
            finally {
                setLoading(false)
            }
        }

        fetchPreferences()
    }, [])

    const handleCreatePreference = async (preferenceData: any) => {
        const response = await fetch(`${API_BASE_URL}/preference`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(preferenceData)
            })

        if (!response.ok) {
            const text = await response.text()
            const textObj = JSON.parse(text)
            throw new Error(`${textObj.message}`)
        }

        const result = await response.json()

        if (result.model) {
            setPreferences(prevPreferences => [...prevPreferences, result.model])
        }
    }

    const handleUpdatePreference = async (preferenceData: { name: string, category: string, description: string }) => {
        if (!selectedPreference) return

        const response = await fetch(`${API_BASE_URL}/preference/${selectedPreference.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                name: preferenceData.name,
                category: preferenceData.category,
                description: preferenceData.description
            })
        })

        if (!response.ok) {
            const text = await response.text()
            const errorText = JSON.parse(text)
            throw new Error(errorText.message);
        }

        const result = await response.json()

        if (result.model) {
            const updatedPreference = result.model
            setPreferences(prev =>
                prev.map(preference =>
                    preference.id === updatedPreference.id ? updatedPreference : preference
                )
            )
        }
    }

    const handleDeletePreferenceConfirm = async () => {
        if (!selectedPreference) return

        setIsDeletingPreference(true)
        try {
            const response = await fetch(`${API_BASE_URL}/preference/${selectedPreference.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                const text = await response.text()
                const errorText = JSON.parse(text)
                throw new Error(errorText.message);
            }

            setPreferences(prev => prev.filter(preference => preference.id !== selectedPreference.id));
            setIsDeletePreferenceModalOpen(false)
            setSelectedPreference(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete Preference')
            setIsDeletePreferenceModalOpen(false)
        } finally {
            setIsDeletingPreference(false)
        }
    }

    const handleEditPreferenceClick = (preference: Preference) => {
        setSelectedPreference(preference)
        setIsEditPreferenceModalOpen(true)
    }

    const handleDeletePreferenceClick = (preference: Preference) => {
        setSelectedPreference(preference)
        setIsDeletePreferenceModalOpen(true)
    }

    const handleDeletePreferenceCancel = () => {
        setIsDeletePreferenceModalOpen(false)
        setSelectedPreference(null)
    }

    const filterPreferences = preferences.filter((preference) => {
        if (filterText === null)
            return true
        const searchText = filterText.toLowerCase()
        return preference.name.toLowerCase().includes(searchText) ||
            (preference.category && preference.category.toLowerCase().includes(searchText)) ||
            (preference.description && preference.description.toLowerCase().includes(searchText))
    })

    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className='mb-4'>
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            Back to Admin Hub
                        </Link>
                    </div>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            {meta.description}
                        </p>
                        {canCreatePreference() && <div className="flex justify-center">
                            <Button onClick={() => setIsCreatePreferenceModalOpen(true)} size="lg">
                                <PlusIcon className="w-5 h-5" />
                                {meta.createButton}
                            </Button>
                        </div>}
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div
                                className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && preferences.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            <p className="text-xl">{meta.noPreferenceMessage}</p>
                        </div>
                    )}

                    {!loading && !error && preferences.length > 0 && (
                        <>
                            <div className="mb-8 flex justify-center">
                                <input
                                    type="text"
                                    placeholder={meta.preferenceSearchPlaceHolder}
                                    value={filterText || ''}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 outline-none"
                                />
                            </div>
                            {filterPreferences.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filterPreferences.map((preference) => (
                                        <PreferenceCard key={preference.id} preference={preference} meta={PreferenceCardMeta}
                                            onDelete={canDeletePreference(preference) ? handleDeletePreferenceClick : undefined} onEdit={canEditPreference(preference) ? handleEditPreferenceClick : undefined} />
                                    ))}
                                </div>
                            ) : (
                                <div>
                                    <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                                        <p className="text-xl">{meta.noPreferenceMessage}</p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                </div>
            </div>

            <PreferenceFormModal
                meta={PreferenceFormModalMeta}
                isOpen={isCreatePreferenceModalOpen}
                onClose={() => {
                    setIsCreatePreferenceModalOpen(false)
                    setSelectedPreference(null)
                }}
                onSubmit={handleCreatePreference}
            />

            <PreferenceFormModal
                meta={{ ...PreferenceFormModalMeta, ...UpdatePreferenceFormMeta }}
                isOpen={isEditPreferenceModalOpen}
                onClose={() => {
                    setIsEditPreferenceModalOpen(false)
                    setSelectedPreference(null)
                }}
                onSubmit={handleUpdatePreference}
                initialPreference={selectedPreference}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeletePreferenceModalOpen}
                onConfirm={handleDeletePreferenceConfirm}
                onCancel={handleDeletePreferenceCancel}
                title={DeletePreferenceMeta.confirmTitle}
                message={DeletePreferenceMeta.confirmMessage}
                isLoading={isDeletingPreference}
            />
        </Fragment>
    )
}
